<script setup lang="ts">
import { computed, onMounted, ref, onUnmounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import FullCalendarComponent from '@fullcalendar/vue3';
import { DatePointApi, EventClickArg } from '@fullcalendar/core';
import AutoComplete, { AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import OverlayPanel from 'primevue/overlaypanel';
import TreeSelect from 'primevue/treeselect';
import AppTabMenu from '@/components/AppTabMenu';
import DocumentedHours from './components/DocumentedHours.vue';
import EventsModals from '@/components/EventsModals';
import { useAuthStore, useCalendarStore, useEventsModalsStore } from '@/store';
import { can, usePersonTypes, useRequestCancel, useRouting } from '@/composables';
import { PERMISSIONS } from '@/enum';
import { FiltersTherapyEventsInterface } from '@/types';

const authStore = useAuthStore();
const { isSwitchTenant } = storeToRefs(authStore);

const calendarStore = useCalendarStore();
const eventsModalsStore = useEventsModalsStore();
const { filters, documentedHoursDateRange } = storeToRefs(calendarStore);
const calendarView = ref<typeof FullCalendarComponent>();

const activeIndex = ref<number>(0);
const router = useRouter();
const selectedDateEnd = ref<Date>(new Date());
const selectedDateStart = ref<Date>(new Date());
const selectedMonth = ref<string>(selectedDateEnd.value.toLocaleString('default', { month: 'long' }));
const selectedYear = ref<number>(selectedDateEnd.value.getFullYear());
const itemsUrl = ['/calendar', '/calendar/day', '/calendar/week', '/calendar/month'];
const items = [
  {
    label: 'List',
  },
  {
    label: 'Day',
  },
  {
    label: 'Week',
  },
  {
    label: 'Month',
  },
];

const route = useRoute();
const filterRef = ref();
const $routing = useRouting();
const attendeeFilter = ref<number | null>();
const filterButtonText = computed<string>(() => {
  const count = calendarStore.filtersAppliedCount;
  return !count ? 'Filters' : `Filters (${count})`;
});
const isClinician = usePersonTypes.isClinician();

const isSupportAdminOrSchoolAdmin = usePersonTypes.isSupportAdminOrSchoolAdmin();
const isSuperAdmin = usePersonTypes.isSuperAdmin();

onMounted(async () => {
  setActiveIndex();
  await clearAndMaybeRefetch();
  if (can([PERMISSIONS.THERAPY_EVENT_INDEX_FILTERS])) {
    await calendarStore.getServices();
    await calendarStore.getEventStatuses();
    await calendarStore.getDisciplines();
    await calendarStore.getOrganizations();
  }

  if (!calendarStore.redirectedToDate && calendarView.value?.calendar.getApi) {
    navigateToToday();
  } else {
    if (calendarView.value?.calendar.getApi) {
      calendarView.value?.calendar.getApi().changeView('timeGridDay', calendarStore.getRedirectedDate());
    }
    if (calendarStore.evenToOpen) {
      eventsModalsStore.openEventModalById(calendarStore.evenToOpen);
    }
  }
});

onBeforeUnmount(() => {
  calendarStore.resetEvents();
  useRequestCancel().requestSaverAbortAll();
  calendarView.value?.calendar.getApi().removeAllEvents();
});

onUnmounted(() => calendarView.value?.calendar.getApi().destroy());

const setActiveIndex = () => {
  itemsUrl.forEach((url, index) => {
    if (route.path.includes(url)) activeIndex.value = index;
  });
};

const updateDayDate = (date: string, month: string, year: number) => {
  calendarView.value?.calendar.getApi().gotoDate(date);
  selectedMonth.value = month;
  selectedYear.value = year;
  activeIndex.value = 1;
};

const toggleFilters = (event: Event) => filterRef.value.toggle(event);

// Disabled due to https://elumatherapy.atlassian.net/browse/TMS-860
// const settings = ref();
// const toggleSettings = (event: Event) => settings.value.toggle(event);

const createNewEventFromCalendar = (info: DatePointApi) => {
  if (can([PERMISSIONS.THERAPY_EVENT_STORE])) {
    eventsModalsStore.preSelectedEventStartAt = info.date;
    eventsModalsStore.createNewEvent();
  }
};

const navigateToToday = () => {
  calendarView.value?.calendar?.getApi()?.today();
  updateCalendar();
};

const navigatePrev = () => {
  calendarView.value?.calendar.getApi().prev();
  updateCalendar();
};

const navigateNext = () => {
  calendarView.value?.calendar.getApi().next();
  updateCalendar();
};

const updateCalendar = () => {
  updateSelectedDate();
  getEvents();
};

const updateSelectedDate = () => {
  calendarStore.currentCalendarView = calendarView.value?.calendar.getApi().view.type;
  const start = calendarView.value?.calendar.getApi().currentData.dateProfile.activeRange.start;
  const end = calendarView.value?.calendar.getApi().currentData.dateProfile.activeRange.end;
  selectedDateStart.value = start;
  selectedDateEnd.value = end;

  selectedMonth.value = calendarView.value?.calendar
    .getApi()
    .currentData.dateProfile.currentRange.end.toLocaleString('default', { month: 'long' });
  selectedYear.value = calendarView.value?.calendar.getApi().currentData.dateProfile.currentRange.end.getFullYear();
  setDocumentedHoursDateRange();
};

// To show an accurate amount for the documented hours, we need to set an alternate date range for
// the documented hours getter to use
const setDocumentedHoursDateRange = () => {
  //if month view
  if (activeIndex.value === 3) {
    let date = new Date(calendarView.value?.calendar.getApi().currentData.dateProfile.currentRange.end);
    date = new Date(date.getFullYear(), date.getMonth(), 1);

    documentedHoursDateRange.value[0] = date.toLocaleDateString('fr-CA').split(',')[0];
    documentedHoursDateRange.value[1] = calendarView.value?.calendar
      .getApi()
      .currentData.dateProfile.currentRange.end.toLocaleDateString('fr-CA')
      .split(',')[0];
  }
};

const organizationAssociated = computed<number[]>(
  () => authStore.organizationAssociated.map((organization) => organization.key) as number[],
);

/**
 * call the api  to get events for the current start/end span
 */
const getEvents = async () => {
  const start = selectedDateStart.value.toLocaleDateString('fr-CA').split(',');
  const end = selectedDateEnd.value.toLocaleDateString('fr-CA').split(',');
  attendeeFilter.value = Number($routing.route.query.attendee);

  const filtersEvents = attendeeFilter.value
    ? {
        start_at: start[0],
        end_at: end[0],
        attendee_id: attendeeFilter.value,
      }
    : {
        start_at: start[0],
        end_at: end[0],
      };

  const filtersEventsToEvaluate: FiltersTherapyEventsInterface = can([PERMISSIONS.THERAPY_EVENT_INDEX_ALL])
    ? { ...filtersEvents }
    : {
        ...filtersEvents,
        organizations: organizationAssociated.value,
      };

  await calendarStore.getEvents(filtersEventsToEvaluate);
};

const onDayView = (date: Date) => {
  calendarStore.redirectedToDate = date;
  router.push(`/calendar/day`);
  activeIndex.value = 1;
};

const handleTabMenuSelect = (index: number) => {
  activeIndex.value = index;
  const attendeeFilterUrl = attendeeFilter.value ? `?attendee=${attendeeFilter.value}` : '';
  const url = `${itemsUrl[index]}${attendeeFilterUrl}`;
  router.push(url);
};

const onDocumentEvent = async (info: EventClickArg) => {
  eventsModalsStore.onDocumentEvent(info.event);
};

const viewMounted = () => updateCalendar();

const onEditEvent = async (info: EventClickArg) => {
  const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);

  if (isSupportAdminOrSchoolAdmin || isSuperAdmin) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  if (isCaseloadManagement) {
    eventsModalsStore.onEditEvent(info.event);
    return;
  }

  if (info.event.classNames.includes('documented')) {
    eventsModalsStore.onDocumentEvent(info.event);
    return;
  }

  eventsModalsStore.onEditEvent(info.event);
};

const clearAndMaybeRefetch = async () => await calendarStore.setDefaultFilters();

const searchEvents = (event: AutoCompleteCompleteEvent) => calendarStore.searchStudentSuggestions(event.query.trim());
</script>

<template>
  <div class="page-container calendar-page-container relative">
    <div class="page-header page-header-with-tabs">
      <h1 class="font-normal">
        <strong>{{ selectedMonth }}</strong>
        {{ selectedYear }}
      </h1>
      <div class="page-header-tab-bar flex align-items-center">
        <AppTabMenu :items="items" :active-index="activeIndex" @tab="handleTabMenuSelect" />
        <AppButton id="calendar-today" type="secondary" text="Today" @click="navigateToToday" />
        <AppButton
          id="calendar-prev"
          class="calendar-arrow-button"
          type="secondary"
          icon-left="chevron_left"
          text=""
          @click="navigatePrev"
        />
        <AppButton
          id="calendar-next"
          class="calendar-arrow-button"
          type="secondary"
          icon-left="chevron_right"
          text=""
          @click="navigateNext"
        />
        <DocumentedHours v-if="isClinician" class="ml-2" />
        <div class="page-header-tab-bar-right">
          <AutoComplete
            v-model="filters.students.selected"
            :multiple="true"
            placeholder="Search Attendees"
            :suggestions="filters.students.options"
            @change="calendarStore.filterEvents"
            @complete="searchEvents($event)"
          />
          <AppButton
            v-if="can([PERMISSIONS.THERAPY_EVENT_INDEX_FILTERS])"
            type="secondary"
            :text="filterButtonText"
            icon-left="filter_list"
            @click="toggleFilters"
          />
          <OverlayPanel v-if="can([PERMISSIONS.THERAPY_EVENT_INDEX_FILTERS])" ref="filterRef">
            <div class="filters-menu">
              <div class="filters-menu-item">
                <p>Event Type</p>
                <Dropdown
                  v-model="filters.eventType.selected"
                  :options="filters.eventType.options"
                  :loading="filters.eventType.loading"
                  @change="calendarStore.filterEvents"
                />
              </div>
              <div class="filters-menu-item">
                <p>Discipline</p>
                <Dropdown
                  v-model="filters.discipline.selected"
                  :options="filters.discipline.options"
                  :loading="filters.discipline.loading"
                  option-label="name"
                  option-value="value"
                  @change="calendarStore.filterEvents"
                />
              </div>
              <div class="filters-menu-item">
                <p>Status</p>
                <Dropdown
                  v-model="filters.eventStatus.selected"
                  :options="filters.eventStatus.options"
                  @change="calendarStore.filterEvents"
                />
              </div>
              <div class="filters-menu-item">
                <p>Organization</p>
                <TreeSelect
                  id="parent_organization"
                  v-model="filters.organization.selected"
                  :options="filters.organization.options"
                  :loading="filters.organization.loading"
                  selection-mode="multiple"
                  display="chip"
                  option-label="label"
                  option-value="key"
                  placeholder="All Organizations"
                  class="w-full font-bold"
                  :show-toggle-all="false"
                  scroll-height="200px"
                  :meta-key-selection="false"
                  @change="calendarStore.filterEvents"
                />
              </div>
              <div class="filters-menu-item">
                <AppButton
                  v-if="calendarStore.isFilterSet"
                  type="secondary"
                  text="Clear All"
                  @click="clearAndMaybeRefetch"
                />
              </div>
              <!-- <p>Service Providers</p>
                <Dropdown
                  v-model="calendarStore.filters.clinician.selected"
                  :options="calendarStore.filters.clinician.options"
                  :loading="calendarStore.filters.clinician.loading"
                  option-label="name"
                  option-value="value"
                  @change="calendarStore.filterEvents"
                />
              </div> -->
            </div>
          </OverlayPanel>
          <!-- NOTE: Disabled due to https://elumatherapy.atlassian.net/browse/TMS-860
          <AppButton type="secondary" text="" icon-left="settings" @click="toggleSettings" />
          <OverlayPanel ref="settings">
            <div class="settings-menu">
              <div class="settings-menu-item">
                <div class="p-2">
                  <a href="#">
                    <span class="relative inline-block text-sm text-800" v-text="'External Calendar Sync'" />
                  </a>
                </div>
              </div>
            </div>
          </OverlayPanel>
          -->
          <AppButton
            v-if="can([PERMISSIONS.THERAPY_EVENT_STORE])"
            type="primary"
            text="New Event"
            icon-left="add"
            @click="() => eventsModalsStore.createNewEvent()"
          />
          <EventsModals />
        </div>
      </div>
    </div>
    <Loader v-if="calendarStore.loading" />
    <div
      v-if="!calendarStore.loading && !calendarStore.events.length"
      class="w-full h-full absolute z-5 flex align-items-center justify-content-center pointer-events-none"
    >
      <p class="surface-100 border-round-lg p-2">No Events in current date range</p>
    </div>
    <router-view v-slot="{ Component }">
      <div :class="{ banner: isSwitchTenant }">
        <component
          :is="Component"
          ref="calendarView"
          @document-event="onDocumentEvent"
          @edit-event="onEditEvent"
          @new-event="createNewEventFromCalendar"
          @day-view="onDayView"
          @update-day="updateDayDate"
          @mounted="viewMounted"
        />
      </div>
    </router-view>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-treeselect) {
  .p-treeselect-label {
    font-weight: bold;
    &.p-placeholder {
      color: $grayDark;
    }
  }

  .p-treeselect-label-container {
    min-width: 15rem;
  }
}
:deep(.p-autocomplete-multiple-container) {
  height: auto;
}
</style>
